import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { ChatBubbleLeftRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { workspaceService, WorkspaceForDocument } from '../../../services/workspaceService';
import { extractAccessErrorMessage } from '../../../utils/featureAccessError';
import { FeatureAccessState } from '../../UI/FeatureAccessState';

interface RemoveFromChatbotModalProps {
  open: boolean;
  documentId: string;
  indexedWorkspaceIds: string[];
  onClose: () => void;
  onConfirm: (workspaceIds: string[]) => void;
  isSubmitting: boolean;
}

export const RemoveFromChatbotModal: React.FC<RemoveFromChatbotModalProps> = ({
  open,
  documentId,
  indexedWorkspaceIds,
  onClose,
  onConfirm,
  isSubmitting,
}) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceForDocument[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isFeatureError, setIsFeatureError] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setSelectedWorkspaceIds([]);
      setErrorMessage('');
      setIsFeatureError(false);
      try {
        const list = await workspaceService.getWorkspacesForDocument(documentId, {
          skipFeatureUnavailableToast: true,
        });
        if (!cancelled) {
          const indexedSet = new Set(indexedWorkspaceIds);
          setWorkspaces(list.filter(w => indexedSet.has(w.id)));
        }
      } catch (error: unknown) {
        if (!cancelled)
        {
          const accessMessage = extractAccessErrorMessage(error);
          if (accessMessage)
          {
            setErrorMessage(accessMessage);
            setIsFeatureError(true);
          }
          else
          {
            setErrorMessage('Não foi possível carregar os agentes. Tente novamente.');
            setIsFeatureError(false);
          }
          setWorkspaces([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [documentId, open, indexedWorkspaceIds]);

  const hasError = !!errorMessage;
  const noAgents = !isLoading && workspaces.length === 0 && !hasError;
  const canConfirm = selectedWorkspaceIds.length > 0 && !isSubmitting;

  const toggleWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceIds(prev => {
      if (prev.includes(workspaceId)) {
        return prev.filter(id => id !== workspaceId);
      }

      return [...prev, workspaceId];
    });
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isSubmitting && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-red-600" />
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      Remover do Chatbot
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                </div>

                <div className="px-6 py-5">
                  {isLoading && (
                    <div className="flex items-center justify-center py-10">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
                    </div>
                  )}

                  {hasError && (
                    <FeatureAccessState
                      variant={isFeatureError ? 'feature' : 'error'}
                      message={errorMessage}
                      compact
                    />
                  )}

                  {noAgents && (
                    <div className="flex flex-col items-center gap-3 py-6 text-center">
                      <div className="flex items-center justify-center h-12 w-12 rounded-full bg-amber-100">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Nenhum agente indexado</p>
                        <p className="mt-1 text-sm text-gray-500">
                          Este documento não está indexado em nenhum agente disponível.
                        </p>
                      </div>
                    </div>
                  )}

                  {!isLoading && workspaces.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-600 mb-4">
                        Selecione os agentes dos quais deseja remover este documento:
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {workspaces.map(workspace => {
                          const isSelected = selectedWorkspaceIds.includes(workspace.id);

                          return (
                            <label
                              key={workspace.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-red-400 bg-red-50'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                value={workspace.id}
                                checked={isSelected}
                                onChange={() => toggleWorkspace(workspace.id)}
                                className="mt-0.5 h-4 w-4 rounded text-red-600 border-gray-300 focus:ring-red-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{workspace.name}</p>
                                {workspace.description && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">{workspace.description}</p>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
                  {(noAgents || hasError) ? (
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Fechar
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => onConfirm(selectedWorkspaceIds)}
                        disabled={!canConfirm}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting && (
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                        )}
                        Confirmar
                      </button>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
